import { useCallback, useEffect, useMemo } from 'react';
import { initializeContract, isVoucherDataExpired, markVouchersStale, selectContractError, selectContractLoading, selectContractProducts, selectContractVouchers, setVouchersData, setVouchersError, setVouchersLoading, useAppDispatch, useAppSelector, } from '@ibk/shared-config';
import { useGetVouchersByContractQuery } from '../services/benefitApi';
const EMPTY_PRODUCTS = [];
const DEFAULT_ERROR = { isError: false, error: null };
export function useActiveContractProducts({ voucherContractId, }) {
    const dispatch = useAppDispatch();
    const contractVouchers = useAppSelector(state => voucherContractId ? selectContractVouchers(state, voucherContractId) : null);
    const productsFromRedux = useAppSelector(state => (voucherContractId ? selectContractProducts(state, voucherContractId) : EMPTY_PRODUCTS)) ||
        EMPTY_PRODUCTS;
    const isLoadingFromRedux = useAppSelector(state => voucherContractId ? selectContractLoading(state, voucherContractId) : false);
    const errorFromRedux = useAppSelector(state => voucherContractId ? selectContractError(state, voucherContractId) : DEFAULT_ERROR);
    const shouldCallApi = useMemo(() => {
        if (!voucherContractId) {
            return false;
        }
        if (!contractVouchers) {
            return true;
        }
        if (contractVouchers.products.length === 0) {
            return true;
        }
        if (!contractVouchers.isDataFresh) {
            return true;
        }
        if (isVoucherDataExpired(contractVouchers.lastFetchTime)) {
            return true;
        }
        return false;
    }, [voucherContractId, contractVouchers]);
    useEffect(() => {
        if (voucherContractId && !contractVouchers) {
            dispatch(initializeContract({ contractId: voucherContractId }));
        }
    }, [voucherContractId, contractVouchers, dispatch]);
    const { data, isLoading: queryLoading, isError: queryError, error: queryErrorDetails, refetch, isFetching, } = useGetVouchersByContractQuery({
        voucherContractId: voucherContractId,
    }, {
        skip: !shouldCallApi,
    });
    useEffect(() => {
        if (voucherContractId && queryLoading !== isLoadingFromRedux) {
            dispatch(setVouchersLoading({ contractId: voucherContractId, isLoading: queryLoading }));
        }
    }, [voucherContractId, queryLoading, isLoadingFromRedux, dispatch]);
    useEffect(() => {
        if (data?.result && voucherContractId) {
            dispatch(setVouchersData({
                contractId: voucherContractId,
                products: data.result,
            }));
        }
    }, [data?.result, voucherContractId, dispatch]);
    useEffect(() => {
        if (voucherContractId && queryError && queryErrorDetails) {
            let errorMessage = 'Erro ao carregar produtos do contrato';
            if ('data' in queryErrorDetails) {
                const errorData = queryErrorDetails.data;
                errorMessage = errorData?.message ?? errorMessage;
            }
            else {
                errorMessage = 'Erro de conexão';
            }
            dispatch(setVouchersError({ contractId: voucherContractId, error: errorMessage }));
        }
    }, [voucherContractId, queryError, queryErrorDetails, dispatch]);
    const handleRefetch = useCallback(async () => {
        if (voucherContractId) {
            dispatch(markVouchersStale({ contractId: voucherContractId }));
            await refetch();
        }
    }, [voucherContractId, dispatch, refetch]);
    const isLoading = isLoadingFromRedux || queryLoading;
    const isError = Boolean(errorFromRedux?.isError) || queryError;
    let error = errorFromRedux?.error ?? null;
    if (!error && queryError && queryErrorDetails) {
        let errorMessage = 'Erro ao carregar produtos do contrato';
        if ('data' in queryErrorDetails) {
            const errData = queryErrorDetails.data;
            errorMessage = errData?.message ?? errorMessage;
        }
        else {
            errorMessage = 'Erro de conexão';
        }
        error = errorMessage;
    }
    const products = productsFromRedux.length > 0 ? productsFromRedux : (data?.result ?? []);
    return {
        products,
        isLoading,
        isRefetching: isFetching && !isLoading,
        isError,
        error,
        refetch: handleRefetch,
    };
}
